import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core';
import { SpinLoading } from '@/components/shared/loadings/spin';

type CancelStatus = 'idle' | 'loading' | 'success' | 'error';

export interface BookingCancelSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmCancel: () => Promise<void>;
  onSuccess?: () => void;
  headerTitle?: string;
  title?: string;
  description?: string;
  cancelLabel?: string;
  keepLabel?: string;
  successText?: string;
  errorText?: string;
  /** Skip the confirm step and open straight into this state — e.g. a remote/automatic cancellation that doesn't need user confirmation. */
  initialStatus?: 'idle' | 'success';
}

export function BookingCancelSheet({
  open,
  onOpenChange,
  onConfirmCancel,
  onSuccess,
  headerTitle,
  title,
  description,
  cancelLabel,
  keepLabel,
  successText,
  errorText,
  initialStatus,
}: BookingCancelSheetProps) {
  const translation = useClientTranslation();

  const [status, setStatus] = useState<CancelStatus>(initialStatus ?? 'idle');

  useEffect(() => {
    if (open && initialStatus) setStatus(initialStatus);
  }, [open, initialStatus]);

  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setStatus('idle'), 300);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (status !== 'success' && status !== 'error') return;
    const timer = setTimeout(() => {
      onOpenChange(false);
      if (status === 'success') setTimeout(() => onSuccess?.(), 300);
    }, 1500);
    return () => clearTimeout(timer);
  }, [status, onOpenChange, onSuccess]);

  async function handleConfirm() {
    setStatus('loading');
    try {
      await onConfirmCancel();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  const isResult = status === 'success' || status === 'error';

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle={false}>
      <DrawerContent>
        {status === 'loading' ? (
          <SpinLoading
            wrapperClassName="min-h-0 flex-1 my-8"
            spinnerClassName="border-jncquoi-neutral-1000"
          />
        ) : isResult ? (
          <div className="animate-sheet-result-in flex items-center gap-2 px-4 py-8">
            {status === 'success' ? (
              <>
                <Icon
                  name="check-circle-solid"
                  width={16}
                  height={16}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {successText ?? translation('App.Labels.BookingCancelled')}
                </p>
              </>
            ) : (
              <>
                <Icon
                  name="warning-circle"
                  width={16}
                  height={16}
                  className="text-jncquoi-neutral-1000 shrink-0"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {errorText ?? translation('App.Labels.BookingError')}
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            <DrawerHeader className="py-8">
              {headerTitle && (
                <p className="font-pp-neue-montreal pb-6 text-center text-[14px] tracking-[0.5px] text-[#1a1a1a] uppercase">
                  {headerTitle}
                </p>
              )}
              <DrawerTitle>{title ?? translation('App.Labels.BookingCancelConfirmTitle')}</DrawerTitle>
              <DrawerDescription>
                {description ?? translation('App.Labels.BookingCancelConfirmText')}
              </DrawerDescription>
            </DrawerHeader>
            <DrawerFooter>
              <Button variant="primary-solid" palette="dark" size="md" onClick={handleConfirm}>
                {cancelLabel ?? translation('App.Labels.CancelBooking')}
              </Button>
              <DrawerClose
                render={
                  <Button variant="tertiary-link" palette="dark" size="sm">
                    {keepLabel ?? translation('App.Labels.KeepBooking')}
                  </Button>
                }
              />
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
